'use client';

import React from 'react';
import Image from 'next/image';
import {
  Ban,
  AlertTriangle,
  Info,
  Backpack,
  type LucideIcon,
} from 'lucide-react';
import { StayExperienceNoticeItem } from '@/types/stayExperienceCardTypes';
import { colors } from '@/design-system/tokens/colors';
import { typography } from '@/design-system/tokens/typography';
import { spacing } from '@/design-system/tokens/spacing';

/** Map notice heading to a semantic icon so the icon matches the heading. */
function getIconForNoticeTitle(title: string): LucideIcon | null {
  const t = title.toLowerCase().trim();
  if (
    t.includes('not allowed') ||
    t.includes('prohibited') ||
    t.includes('restricted')
  ) {
    return Ban;
  }
  if (
    t.includes('not suitable') ||
    t.includes('unsuitable') ||
    t.includes('not recommended')
  ) {
    return AlertTriangle;
  }
  if (
    t.includes('important') ||
    t.includes('information') ||
    t.includes('required') ||
    t.includes('valid id') ||
    t.includes('permits')
  ) {
    return Info;
  }
  if (
    t.includes('what to bring') ||
    t.includes('bring') ||
    t.includes('pack') ||
    t.includes('essentials')
  ) {
    return Backpack;
  }
  return null;
}

const ICON_SIZE = 20;
const ICON_PADDING_PX = 8; // spacing[2] = 8px

interface NoticeItem {
  title: string;
  description: string;
  icon?: string;
  imageUrl?: string;
  _id?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface NoticesProps {
  options:
    | NoticeItem[]
    | StayExperienceNoticeItem[]
    | Array<{
        title: string;
        description: string;
        imageUrl?: string;
        _id?: string;
        [key: string]: unknown;
      }>;
  title?: string;
}

const Notices: React.FC<NoticesProps> = ({ options, title }) => {
  if (!options || !Array.isArray(options) || options.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4], // 16px gap between title and items
      }}
    >
      {/* Title */}
      <h2
        style={{
          fontFamily: typography.fonts.primary,
          fontSize: typography.size.lg, // 18px
          fontWeight: typography.weight.semibold,
          color: colors.typography.primary, // #1c1917
          lineHeight: typography.lineHeight.none,
          margin: 0,
        }}
      >
        {title || 'Notices'}
      </h2>

      {/* Notice Items */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[2], // 8px gap between items
        }}
      >
        {options.map((option, index) => {
          const SemanticIcon = getIconForNoticeTitle(option.title);
          const iconUrl =
            !SemanticIcon && (option.icon || option.imageUrl)
              ? option.icon || option.imageUrl
              : null;

          return (
            <div
              key={option._id || index}
              style={{
                display: 'flex',
                gap: '7px', // 7px gap between icon and text (specific from Figma)
                alignItems: 'center',
                paddingRight: spacing[4], // 16px padding right
              }}
            >
              {/* Icon Container */}
              <div
                style={{
                  backgroundColor: '#f8f9f8', // Light grey background (not in design system but matches Figma)
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: spacing[2], // 8px padding
                  borderRadius: '8px',
                  flexShrink: 0,
                  width: ICON_SIZE + ICON_PADDING_PX * 2,
                  height: ICON_SIZE + ICON_PADDING_PX * 2,
                }}
              >
                {SemanticIcon ? (
                  <SemanticIcon
                    size={ICON_SIZE}
                    style={{ color: colors.typography.primary }}
                    aria-hidden
                  />
                ) : iconUrl ? (
                  <Image
                    src={iconUrl}
                    alt={option.title}
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                  />
                ) : (
                  <Info
                    size={ICON_SIZE}
                    style={{ color: colors.typography.primary }}
                    aria-hidden
                  />
                )}
              </div>

              {/* Text Content */}
              <div
                style={{
                  flex: '1 0 0',
                  minWidth: 0,
                }}
              >
                <p
                  style={{
                    fontFamily: typography.fonts.primary,
                    fontSize: typography.size.sm, // 14px
                    fontWeight: typography.weight.regular,
                    color: colors.typography.tertiary, // #78716c
                    lineHeight: typography.lineHeight.normal,
                    margin: 0,
                  }}
                >
                  <span
                    style={{
                      fontWeight: typography.weight.bold,
                      color: colors.typography.primary, // #1c1917 for bold label
                    }}
                  >
                    {option.title}:
                  </span>{' '}
                  <span>{option.description}</span>
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Notices;
